import { renderEmbeddedEntry, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Body, Container, Heading, Subhead } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { ContentfulImage } from "../../../ContentfulImage";
import { Icon } from "../../../Icon";
import { CustomCSSProperties, TileProps } from "../../Tile.types";
// @ts-ignore
import styles from "./Default.module.scss";

export const Default: React.FC<TileProps> = ({
  content,
  padded,
  variation,
  desktopOrientation,
  mobileOrientation,
  desktopLayoutOrientation,
  mobileLayoutOrientation,
  stackedLayoutPadding,
  heading,
  subhead,
  body,
  className,
  style: styleProps,
  textAlign,
  phoneNumber,
  parseMetadata,
  inspector,
}) => {
  const fields = content?.fields || {};
  const style: CustomCSSProperties = { ...styleProps };

  if (content?.fields?.background_color) style["--tile-background-color"] = `var(${content?.fields?.background_color})`;
  if (content?.fields?.image_aspect_ratio) style["--tile-image-aspect-ratio"] = content?.fields?.image_aspect_ratio;

  const handleClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const anchor = target.tagName === "A" ? target : target.closest("a");

    if (anchor) {
      e.preventDefault();
      const uniqueId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      EventClient.elementClicked({
        webElement: {
          elementType: "Tile Link",
          htmlId: (anchor as HTMLAnchorElement).href,
          location: window.location.href,
          text: (anchor as HTMLAnchorElement).innerText,
          name: uniqueId,
        },
      });

      const urlParams = new URLSearchParams(window.location.search);
      urlParams.append("utm_subId1", uniqueId);
      const redirectUrl = `${(anchor as HTMLAnchorElement).href}?${urlParams.toString()}`;
      window.open(redirectUrl, "_blank");
    }
  };

  return (
    <Container
      flex
      direction={mobileOrientation === "vertical" ? "column" : "row"}
      alignItems="center"
      lg={{
        direction: desktopOrientation === "vertical" ? "column" : "row",
      }}
      style={style}
      className={cn(
        className,
        styles.tile,
        styles[`mobile-${mobileOrientation}`],
        styles[`desktop-${desktopOrientation}`],
        styles[`mobile-layout-${mobileLayoutOrientation}`],
        styles[`desktop-layout-${desktopLayoutOrientation}`],
        styles[`stacked-layout-padding-${stackedLayoutPadding}`],
        {
          [styles.padded]: padded,
          [styles.textAlignLeft]: textAlign === "left",
        }
      )}
      onClick={handleClick}
    >
      {(fields.icon || fields.image) && (
        <div className={styles.slot}>
          {!fields.image && fields.icon && (
            <Icon
              name={fields.icon}
              size={fields.icon_size}
              className={styles.icon}
              {...inspector({
                entryId: content.sys.id,
                fieldId: "icon",
              })}
            />
          )}
          {fields.image && (
            <ContentfulImage
              image={fields.image}
              config={fields.image_config}
              className={styles.image}
              {...inspector({
                entryId: content.sys.id,
                fieldId: "image",
              })}
              style={style}
            />
          )}
        </div>
      )}
      {fields?.eyebrow &&
        typeof fields?.eyebrow === "object" &&
        documentToReactComponents(fields?.eyebrow, {
          renderMark,
          renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => (
              <Subhead
                type="span"
                className={styles.eyebrow}
                variant="subhead-sm"
                modifier={500}
                {...inspector({
                  entryId: content.sys.id,
                  fieldId: "eyebrow",
                })}
              >
                {children}
              </Subhead>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node) =>
              renderEmbeddedEntry(node, "Tile Eyebrow", fields?.eyebrow, phoneNumber, parseMetadata),
          },
        })}
      {fields?.heading &&
        typeof fields?.heading === "object" &&
        documentToReactComponents(fields?.heading, {
          renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => (
              <Heading
                className={styles.heading}
                variant="heading-xl"
                {...heading}
                {...inspector({
                  entryId: content.sys.id,
                  fieldId: "heading",
                })}
              >
                {children}
              </Heading>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node) => {
              const iconSize = !heading?.variant ? "md" : undefined;
              return renderEmbeddedEntry(node, "Tile Heading", fields?.heading, phoneNumber, parseMetadata, iconSize);
            },
          },
        })}
      {fields?.subhead &&
        typeof fields?.subhead === "object" &&
        documentToReactComponents(fields?.subhead, {
          renderMark,
          renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => (
              <Subhead
                variant="subhead-md"
                {...subhead}
                className={cn(styles.subhead)}
                {...inspector({
                  entryId: content.sys.id,
                  fieldId: "subhead",
                })}
              >
                {children}
              </Subhead>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node) =>
              renderEmbeddedEntry(node, "Tile Subhead", fields?.subhead, phoneNumber, parseMetadata),
          },
        })}
      {fields?.body &&
        documentToReactComponents(fields?.body, {
          renderMark,
          renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => (
              <Body
                variant="body-md"
                {...body}
                className={cn(styles.body, {
                  [styles.badgeBody]: variation === "badge",
                })}
                {...inspector({
                  entryId: content.sys.id,
                  fieldId: "body",
                })}
              >
                {children}
              </Body>
            ),
            [INLINES.EMBEDDED_ENTRY]: (node) =>
              renderEmbeddedEntry(node, "Tile Body", fields?.body, phoneNumber, parseMetadata),
          },
        })}
    </Container>
  );
};
